import { useEffect } from 'react';
const SRC = 'use-timestamp-range.ts';

import { useAppSelector, useAppDispatch } from 'store/store-hooks';
import { createClipId, getClipTime, parseClipId, selectClipData } from 'components/app/_helpers/_app.helper';
import { Clip } from 'components/app/editor/_mediator/editor.slice';
import { getLinkData } from 'components/app/editor/_mediator/editor-clips/editor-clips-linking.helper';
import { ClipNeighbors, setTsLinkedPayload } from './timestamp.slice';

export const useTimeStampLinkedPayload = () => {
  const selectedFile = useAppSelector((state) => state.projects.selectedFile);
  const currentPlayerDuration = useAppSelector((state) => state.timestamp.currentPlayerDuration);
  const linkedClips = useAppSelector((state) => state.editor.linkedClips);
  const activeClip = useAppSelector((state) => state.editor.activeClip);
  const selector = useAppSelector((state) => state.editor.selector);
  const clipsMap = useAppSelector((state) => state.editor.clipsMap);
  const dispatch = useAppDispatch();

  const getNeighbors = (clipId: ClipId): ClipNeighbors => {
    const fileClips = selector![parseClipId(clipId).mediaFileId];
    const index = fileClips.findIndex((id) => id === clipId);
    if (index === -1) {
      console.error(`[${SRC}] getNeighborClips`);
      return {
        leftNeighbor: null,
        rightNeighbor: null,
      };
    }
    if (index === 0)
      return {
        leftNeighbor: null,
        rightNeighbor: selectClipData(fileClips[index + 1], clipsMap!),
      };
    if (index === fileClips.length - 1)
      return {
        rightNeighbor: null,
        leftNeighbor: selectClipData(fileClips[index - 1], clipsMap!),
      };
    return {
      leftNeighbor: selectClipData(fileClips[index - 1], clipsMap!),
      rightNeighbor: selectClipData(fileClips[index + 1], clipsMap!),
    };
  };

  const getRange = ({ leftNeighbor, rightNeighbor }: ClipNeighbors) => {
    if (leftNeighbor !== null && rightNeighbor !== null)
      return {
        minTime: getClipTime(leftNeighbor, 'inverse'),
        maxTime: getClipTime(rightNeighbor),
      };
    if (rightNeighbor !== null)
      return {
        minTime: 0,
        maxTime: getClipTime(rightNeighbor),
      };
    if (leftNeighbor !== null)
      return {
        minTime: getClipTime(leftNeighbor, 'inverse'),
        maxTime: currentPlayerDuration!,
      };
    else {
      console.error(`[${SRC}] useTimestampPayload`);
      return {
        minTime: 0,
        maxTime: currentPlayerDuration!,
      };
    }
  };

  const getPayload = (clip: Clip) => {
    const { leftNeighbor, rightNeighbor } = getNeighbors(clip.clipId);
    const range = getRange({ leftNeighbor, rightNeighbor });
    const linkData = getLinkData(clip, linkedClips);
    if (!linkData) return;

    const boundaries = {
      boundaryLeft: leftNeighbor?.clipId ?? null,
      boundaryRight: rightNeighbor?.clipId ?? null,
    };

    if (clip.startTime !== null && leftNeighbor?.endTime === clip.startTime) {
      if (leftNeighbor.startTime) range.minTime = leftNeighbor.startTime;
      else {
        const clipNeighbors = getNeighbors(createClipId(leftNeighbor));
        boundaries.boundaryLeft = clipNeighbors.leftNeighbor?.clipId ?? null;
        range.minTime = getRange(clipNeighbors).minTime;
      }
    }
    if (clip.endTime !== null && rightNeighbor?.startTime === clip.endTime) {
      if (rightNeighbor.endTime) range.maxTime = rightNeighbor.endTime;
      else {
        const clipNeighbors = getNeighbors(createClipId(rightNeighbor));
        boundaries.boundaryRight = clipNeighbors.rightNeighbor?.clipId ?? null;
        range.maxTime = getRange(clipNeighbors).maxTime;
      }
    }
    return { ...range, leftNeighbor, rightNeighbor, ...linkData, ...boundaries };
  };

  useEffect(() => {
    if (!currentPlayerDuration) return;
    if (!clipsMap) return;
    if (!activeClip) {
      dispatch(setTsLinkedPayload(null));
      return;
    }
    if (parseClipId(activeClip).mediaFileId !== selectedFile) return;
    // NOTE: return because the `currentPlayerDuration` will fire next
    const clip = selectClipData(activeClip, clipsMap);
    if (clip.clipGroupId) {
      const payload = getPayload(clip);
      if (payload) dispatch(setTsLinkedPayload(payload));
    } else dispatch(setTsLinkedPayload(null));
  }, [activeClip, currentPlayerDuration, activeClip && clipsMap?.[activeClip]]);
};
